import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForklifts } from '@/hooks/useForklifts'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import type { Forklift } from '@/types/forklift'
import {
  STATUS_COLORS,
  STATUS_LABELS,
  DEFAULT_PAGE_SIZE,
} from '@/config/constants'

const PERIOD_OPTIONS = [
  { value: '7d', label: '過去7日間' },
  { value: '30d', label: '過去30日間' },
  { value: '90d', label: '過去90日間' },
  { value: '365d', label: '過去1年間' },
  { value: 'all', label: '全期間' },
]

type SortKey = 'fork_id' | 'site_id' | 'status' | 'last_seen'
type SortDirection = 'asc' | 'desc'

export default function ForkliftListPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [period, setPeriod] = useState<string>('all')
  const [sortKey, setSortKey] = useState<SortKey>('last_seen')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [showSimOnly, setShowSimOnly] = useState(false)

  const { data, isLoading, isFetching, error } = useForklifts({
    page,
    limit: DEFAULT_PAGE_SIZE,
    status: statusFilter || undefined,
    period,
    show_sim: showSimOnly ? 'true' : 'false',
  })

  const rawForklifts = data?.data || []
  const pagination = data?.pagination

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDirection(key === 'last_seen' ? 'desc' : 'asc')
    }
  }

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''

  const forklifts = useMemo(() => {
    const sorted = [...rawForklifts]
    sorted.sort((a: Forklift, b: Forklift) => {
      let cmp = 0
      switch (sortKey) {
        case 'fork_id':
        case 'site_id':
        case 'status':
          cmp = (a[sortKey] ?? '').localeCompare(b[sortKey] ?? '')
          break
        case 'last_seen':
          cmp = parseInt(a.last_seen, 10) - parseInt(b.last_seen, 10)
          break
      }
      return sortDirection === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [rawForklifts, sortKey, sortDirection])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">
            フォークリフト一覧
          </h1>
          {isFetching && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <LoadingSpinner size="sm" />
              <span>読み込み中...</span>
            </div>
          )}
        </div>

        {/* フィルター */}
        <div className="flex items-center gap-4">
          {/* 期間選択 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">期間:</label>
            <select
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value)
                setPage(1)
              }}
              disabled={isFetching}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* ステータスフィルター */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            disabled={isFetching}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          >
            <option value="">すべてのステータス</option>
            <option value="operating">稼働中</option>
            <option value="idle">アイドル</option>
            <option value="offline">オフライン</option>
          </select>

          {/* シミュレータ表示切替 */}
          <button
            onClick={() => {
              setShowSimOnly((v) => !v)
              setPage(1)
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showSimOnly
                ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {showSimOnly ? 'SIM 表示中' : 'SIM 非表示'}
          </button>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="text-center py-8 text-red-600 bg-red-50 rounded-lg">
          データの取得に失敗しました
        </div>
      )}

      {/* テーブル */}
      <div
        className={`bg-white rounded-lg shadow overflow-hidden relative ${isFetching ? 'opacity-60' : ''}`}
      >
        {/* 初回ローディング時のオーバーレイ */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
            <div className="flex flex-col items-center gap-2">
              <LoadingSpinner size="lg" />
              <span className="text-gray-600">データを取得中...</span>
            </div>
          </div>
        )}

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {(
                [
                  ['fork_id', 'フォークリフトID'],
                  ['site_id', 'サイト'],
                  ['status', 'ステータス'],
                  ['last_seen', '最終更新'],
                ] as [SortKey, string][]
              ).map(([key, label]) => (
                <th
                  key={key}
                  onClick={() => handleSort(key)}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
                >
                  {label}
                  {sortIndicator(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {forklifts.length === 0 && !isLoading ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  該当するフォークリフトがありません
                </td>
              </tr>
            ) : (
              forklifts.map((forklift) => (
                <tr
                  key={forklift.fork_id}
                  onClick={() => navigate(`/forklifts/${forklift.fork_id}`)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-gray-900">
                      {forklift.fork_id}
                    </span>
                    {(forklift.fork_id.startsWith('sim-') ||
                      forklift.fork_id.startsWith('fork_sim_')) && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700">
                        SIM
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {forklift.site_id}
                    {(forklift.site_id === 'web_simulator' ||
                      forklift.site_id.startsWith('sim-')) && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700">
                        SIM
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: `${STATUS_COLORS[forklift.status]}20`,
                        color: STATUS_COLORS[forklift.status],
                      }}
                    >
                      {STATUS_LABELS[forklift.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {(() => {
                      const date = new Date(parseInt(forklift.last_seen, 10))
                      const dayOfWeek = [
                        '日',
                        '月',
                        '火',
                        '水',
                        '木',
                        '金',
                        '土',
                      ][date.getDay()]
                      return `${date.toLocaleDateString('ja-JP')}(${dayOfWeek}) ${date.toLocaleTimeString('ja-JP')}`
                    })()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ページネーション */}
      {pagination && pagination.total > DEFAULT_PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {pagination.total} 件中 {(page - 1) * DEFAULT_PAGE_SIZE + 1} -{' '}
            {Math.min(page * DEFAULT_PAGE_SIZE, pagination.total)} 件を表示
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              前へ
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page * DEFAULT_PAGE_SIZE >= pagination.total}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              次へ
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
